import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { useState } from 'react';
import { clsx } from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { signalsApi, billingApi, ApiError, type Signal, type SignalContext, type PositionAtTime } from '~/lib/api';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { InlineError } from '~/components/ErrorPage';
import { EditSignalModal } from '~/components/EditSignalModal';
import { UpgradePrompt } from '~/components/UpgradePrompt';
import { useAuth } from '~/contexts/AuthContext';

/**
 * Parse either a DB UTC timestamp ("YYYY-MM-DD HH:MM:SS") or an ISO 8601
 * string (with or without Z/offset) into a Date. Signal.created_at uses the
 * former; message.timestamp (from Discord's datetime attribute) is the latter.
 */
function parseUtc(ts: string): Date {
  if (!ts) return new Date(NaN);
  // ISO 8601 already includes the "T" separator — use verbatim.
  if (ts.includes('T')) return new Date(ts);
  // DB format: "YYYY-MM-DD HH:MM:SS" (UTC, no zone marker).
  return new Date(ts.replace(' ', 'T') + 'Z');
}

function formatTime(ts: string): string {
  return parseUtc(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateTime(ts: string): string {
  return parseUtc(ts).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelative(ts: string): string {
  return formatDistanceToNow(parseUtc(ts), { addSuffix: true });
}

const actionColor: Record<string, string> = {
  BTO: 'text-profit',
  STC: 'text-loss',
  trim: 'text-warning',
  watch: 'text-blue-400',
  none: 'text-text-secondary',
  price_update: 'text-text-secondary',
};

function PlayDetailContent() {
  const { id } = useParams<{ id: string }>();
  const { user, isAdmin } = useAuth();
  const [editingSignal, setEditingSignal] = useState<Signal | null>(null);

  const {
    data: threadData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['thread', id],
    queryFn: () => signalsApi.threadSignals(id!),
    enabled: !!id,
  });

  const { data: billing } = useQuery({
    queryKey: ['billing-status'],
    queryFn: billingApi.status,
  });
  const hasActiveSubscription = billing?.isActive ?? false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <InlineError
        error={error instanceof ApiError ? error : (error as Error)}
      />
    );
  }

  const thread = threadData?.thread;
  const signals = threadData?.signals || [];

  if (!thread || signals.length === 0) {
    return (
      <div className="card p-8 text-center">
        <p className="text-text-secondary">Play not found.</p>
        <Link to="/" className="text-accent text-sm hover:underline mt-2 inline-block">
          ← Back to plays
        </Link>
      </div>
    );
  }

  const isClosed = thread.thread_status === 'closed';
  const isExpired = thread.thread_status === 'expired';
  const isWatching = thread.thread_status === 'watching';
  const hasProfit = thread.pnl_percent !== null && thread.pnl_percent >= 0;
  const canEdit = isAdmin || user?.username === thread.trader_username;
  // Edit operates on the position's entry signal (BTO preferred, fall back to watch).
  // Same pattern as TradeThreadRow's expanded view — EditSignalModal's field changes
  // propagate to the position via the /api/signals/:id PATCH.
  const entrySignal =
    signals.find((s) => s.action === 'BTO') ?? signals.find((s) => s.action === 'watch') ?? null;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Breadcrumb */}
      <Link to="/" className="text-text-muted text-sm hover:text-text-primary inline-flex items-center gap-1">
        ← Back to plays
      </Link>

      {/* Position header */}
      <div className="card p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-2xl sm:text-3xl font-bold font-mono text-text-primary">
                {thread.ticker} {thread.strike}{thread.option_type}
              </span>
              <span className="text-text-secondary text-sm">
                {thread.expiry || (thread.expiry_type === '0dte' ? '0DTE' : '')}
              </span>
            </div>
            <div className="mt-2 text-sm text-text-muted">
              <Link
                to={`/traders/${thread.trader_id}`}
                className="font-medium text-text-primary hover:text-accent transition-colors"
              >
                {thread.trader_username}
              </Link>
              {thread.channel_name && <span className="text-text-muted"> · #{thread.channel_name}</span>}
              <span className="text-text-muted"> · {thread.signal_count} signal{thread.signal_count !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isClosed ? (
              <span
                className={clsx(
                  'px-2 py-1 rounded text-sm font-mono font-medium',
                  hasProfit ? 'bg-profit/10 text-profit' : 'bg-loss/10 text-loss'
                )}
              >
                {thread.pnl_percent !== null
                  ? `${hasProfit ? '+' : ''}${thread.pnl_percent.toFixed(0)}%`
                  : 'Closed'}
              </span>
            ) : isExpired ? (
              <span className="px-2 py-1 rounded text-sm font-mono font-medium bg-surface-3 text-text-secondary">
                Expired
              </span>
            ) : isWatching ? (
              <span className="px-2 py-1 rounded text-sm font-mono font-medium bg-warning/10 text-warning">
                Watching
              </span>
            ) : (
              <span className="px-2 py-1 rounded text-sm font-mono font-medium bg-blue-500/10 text-blue-400">
                Active
              </span>
            )}
            {canEdit && entrySignal && (
              <button
                type="button"
                onClick={() => setEditingSignal(entrySignal)}
                className="px-3 py-1 rounded text-sm font-mono bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-text-muted text-xs uppercase font-mono tracking-wider">Entry</div>
            <div className="font-mono text-text-primary mt-0.5">
              {thread.entry_price ? `$${thread.entry_price.toFixed(2)}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-text-muted text-xs uppercase font-mono tracking-wider">Exit</div>
            <div className="font-mono text-text-primary mt-0.5">
              {thread.exit_price ? `$${thread.exit_price.toFixed(2)}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-text-muted text-xs uppercase font-mono tracking-wider">Opened</div>
            <div className="font-mono text-text-primary mt-0.5">{formatRelative(thread.created_at)}</div>
          </div>
          <div>
            <div className="text-text-muted text-xs uppercase font-mono tracking-wider">Updated</div>
            <div className="font-mono text-text-primary mt-0.5">{formatRelative(thread.updated_at)}</div>
          </div>
        </div>
      </div>

      {editingSignal && (
        <EditSignalModal
          signal={editingSignal}
          positionId={thread.position_id}
          positionStatus={thread.position_status}
          positionEntryPrice={thread.entry_price}
          onClose={() => setEditingSignal(null)}
        />
      )}

      {/* Paywall for non-subscribers — they see the timeline rows but the
          per-signal LLM context is premium. */}
      {!hasActiveSubscription && (
        <div className="card p-6 sm:p-8 flex items-center justify-center">
          <UpgradePrompt
            headline="LLM decision context is a premium feature"
            subline="See the messages, positions, and reasoning behind every parse"
            buttonLabel="Upgrade to Unlock Context"
          />
        </div>
      )}

      {/* Signals feed. Expanding a row to see LLM context requires active
          subscription; for non-subs, rows are read-only summaries. */}
      <div className="space-y-3">
        {signals.map((signal) => (
          <SignalContextCard
            key={signal.id}
            signal={signal}
            canLoadContext={hasActiveSubscription}
          />
        ))}
      </div>
    </div>
  );
}

function SignalContextCard({
  signal,
  canLoadContext,
}: {
  signal: Signal;
  canLoadContext: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card overflow-hidden">
      {/* Signal header */}
      <button
        type="button"
        onClick={() => canLoadContext && setExpanded(!expanded)}
        disabled={!canLoadContext}
        className={clsx(
          'w-full flex items-center gap-3 px-4 py-3 text-left',
          canLoadContext && 'hover:bg-surface-2 transition-colors',
          !canLoadContext && 'cursor-default'
        )}
      >
        <span
          className={clsx(
            'text-xs font-mono font-medium uppercase tracking-wider px-1.5 py-0.5 rounded',
            actionColor[signal.action] ?? 'text-text-secondary'
          )}
        >
          {signal.action}
        </span>
        {signal.signal_price > 0 && (
          <span className="font-mono text-text-primary">${signal.signal_price.toFixed(2)}</span>
        )}
        <span className="text-text-muted text-xs">{formatDateTime(signal.created_at)}</span>
        {signal.confidence != null && (
          <span className="text-text-muted text-xs font-mono">· {signal.confidence}%</span>
        )}
        {canLoadContext && (
          <span className="ml-auto text-text-muted text-xs font-mono">
            {expanded ? '▼' : '▶'}
          </span>
        )}
      </button>

      {expanded && canLoadContext && <SignalContextBody signalId={signal.id} />}
    </div>
  );
}

function SignalContextBody({ signalId }: { signalId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['signal-context', signalId],
    queryFn: () => signalsApi.signalContext(signalId),
  });

  if (isLoading) {
    return <div className="px-4 py-3 text-text-muted text-sm">Loading context…</div>;
  }

  if (error) {
    const apiErr = error instanceof ApiError ? error : null;
    if (apiErr?.isSubscriptionRequired) {
      return (
        <div className="px-4 py-4">
          <UpgradePrompt
            headline="LLM decision context is a premium feature"
            buttonLabel="Upgrade to Unlock"
          />
        </div>
      );
    }
    return (
      <div className="px-4 py-3 text-sm text-loss">
        {apiErr?.message || (error as Error).message}
      </div>
    );
  }

  if (!data) return null;

  return <ContextSections ctx={data} />;
}

function ContextSections({ ctx }: { ctx: SignalContext }) {
  return (
    <div className="border-t border-border divide-y divide-border">
      {/* Parsed message */}
      <Section title="Parsed message" meta={formatTime(ctx.currentMessage.timestamp)}>
        <pre className="whitespace-pre-wrap font-mono text-xs text-text-secondary leading-relaxed">
          {ctx.currentMessage.content || '(empty)'}
        </pre>
      </Section>

      {/* LLM response — raw output for debugging. Backend filters the batched
          response down to this message's entries. The canonical parsed fields
          live on the signal row, not here. */}
      <Section
        title="LLM response"
        meta={
          ctx.apiCall
            ? `${ctx.apiCall.model} · ${ctx.apiCall.input_tokens ?? '?'}→${ctx.apiCall.output_tokens ?? '?'} tok${
                ctx.apiCall.duration_ms != null ? ` · ${ctx.apiCall.duration_ms}ms` : ''
              }`
            : 'no LLM call'
        }
      >
        {ctx.apiCall == null ? (
          <p className="text-text-muted text-xs italic">
            Rule-based skip — no LLM was called for this signal.
          </p>
        ) : ctx.llmResponse == null ? (
          <p className="text-text-muted text-xs italic">Response could not be parsed.</p>
        ) : (
          <LlmResponseView response={ctx.llmResponse} />
        )}
      </Section>

      {/* Positions the LLM had in its prompt context at parse time.
          Status pill reflects current state — a position that was live at T
          but has since closed still shows up here (that's the point of this
          debug view) but reads "closed". */}
      <Section
        title="Positions in LLM context"
        meta={
          ctx.positionsAtParseTime.length === 0
            ? 'none'
            : `${ctx.positionsAtParseTime.length} position${ctx.positionsAtParseTime.length !== 1 ? 's' : ''}`
        }
      >
        {ctx.positionsAtParseTime.length === 0 ? (
          <p className="text-text-muted text-xs italic">No positions from this trader were in the LLM context.</p>
        ) : (
          <div className="space-y-2">
            {ctx.positionsAtParseTime.map((p) => (
              <PositionCard key={p.id} pos={p} />
            ))}
          </div>
        )}
      </Section>

      {/* Lead-up messages */}
      <Section
        title="Lead-up (last 60 min)"
        meta={
          ctx.leadUpMessages.length === 0
            ? 'none'
            : `${ctx.leadUpMessages.length} message${ctx.leadUpMessages.length !== 1 ? 's' : ''}`
        }
      >
        {ctx.leadUpMessages.length === 0 ? (
          <p className="text-text-muted text-xs italic">No earlier messages from this author in the 60-min window.</p>
        ) : (
          <pre className="whitespace-pre-wrap font-mono text-xs text-text-secondary leading-relaxed">
            {ctx.leadUpMessages
              .map((m) => `[${formatTime(m.timestamp)}] ${m.content}`)
              .join('\n')}
          </pre>
        )}
      </Section>
    </div>
  );
}

function Section({
  title,
  meta,
  children,
}: {
  title: string;
  meta?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="text-xs font-mono font-medium uppercase tracking-widest text-text-muted">
          {title}
        </h3>
        {meta && <span className="text-text-muted text-xs font-mono">{meta}</span>}
      </div>
      {children}
    </div>
  );
}

// The prompt asks the LLM for short field names ("a", "t", "k", …) to
// shrink output tokens. The raw response is what's stored in api_calls, so
// the dashboard has to accept both forms. Backend signal-parser.ts has the
// authoritative mapping — keep this in sync.
function unshortenSignal(s: Record<string, unknown>): Record<string, unknown> {
  return {
    ...s,
    message_index: s.message_index ?? s.mi,
    action: s.action ?? s.a,
    ticker: s.ticker ?? s.t,
    strike: s.strike ?? s.k,
    option_type: s.option_type ?? s.o,
    expiry_spec: s.expiry_spec ?? s.e,
    price: s.price ?? s.p,
    confidence: s.confidence ?? s.c,
    size: s.size ?? s.sz,
    interpretation: s.interpretation ?? s.n,
  };
}

function LlmResponseView({ response }: { response: unknown }) {
  // Schema: { signals: [ { action, ticker, strike, option_type, expiry_spec,
  //   price, confidence, interpretation, ... } ] } — already filtered server-side
  // to this message's signals via message_index matching.
  const parsed = response as { signals?: Array<Record<string, unknown>> } | null;
  const signals = (parsed?.signals ?? []).map(unshortenSignal);

  if (signals.length === 0) {
    return <p className="text-text-muted text-xs italic">No signals in response.</p>;
  }

  return (
    <div className="space-y-4">
      {signals.map((s, i) => (
        <LlmSignalBlock key={i} signal={s} index={i} showIndex={signals.length > 1} />
      ))}
    </div>
  );
}

function LlmSignalBlock({
  signal,
  index,
  showIndex,
}: {
  signal: Record<string, unknown>;
  index: number;
  showIndex: boolean;
}) {
  const expirySpec = signal.expiry_spec as Record<string, unknown> | null | undefined;
  const expiryDisplay = formatExpirySpec(expirySpec);
  const action = typeof signal.action === 'string' ? signal.action : '—';
  const originalTicker = typeof signal.original_ticker === 'string' ? signal.original_ticker : null;

  return (
    <div className="space-y-2">
      {showIndex && (
        <div className="text-text-muted text-[10px] font-mono uppercase tracking-wider">
          Signal {index + 1}
        </div>
      )}
      <div className="flex items-baseline gap-3 flex-wrap">
        <span
          className={clsx(
            'text-sm font-mono font-semibold uppercase',
            actionColor[action] ?? 'text-text-secondary'
          )}
        >
          {action}
        </span>
        <span className="font-mono text-text-primary">
          {stringifyOr(signal.ticker, '?')} {stringifyOr(signal.strike, '?')}
          {stringifyOr(signal.option_type, '')}
        </span>
        {expiryDisplay && <span className="text-text-secondary text-xs font-mono">{expiryDisplay}</span>}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs font-mono">
        <KV k="price" v={formatPrice(signal.price)} />
        <KV k="confidence" v={signal.confidence != null ? `${signal.confidence}%` : '—'} />
      </div>
      {originalTicker && originalTicker !== signal.ticker && (
        <div className="text-xs font-mono text-text-muted">
          original ticker: <span className="text-text-secondary">{originalTicker}</span>
        </div>
      )}
      {typeof signal.interpretation === 'string' && signal.interpretation.length > 0 && (
        <p className="text-text-secondary text-xs italic leading-relaxed pt-1">
          "{signal.interpretation}"
        </p>
      )}
    </div>
  );
}

/**
 * Compact linked card: ticker, strike, expiry, status pill. Whole card is
 * a link to that position's detail page. No commentary fields.
 */
function PositionCard({ pos }: { pos: PositionAtTime }) {
  const expiry = pos.expiry || (pos.expiry_type === '0dte' ? '0DTE' : null);
  const statusColor =
    pos.status === 'open'
      ? 'text-profit bg-profit/10'
      : pos.status === 'watching' || pos.status === 'partial'
        ? 'text-warning bg-warning/10'
        : pos.status === 'closed' || pos.status === 'expired' || pos.status === 'dismissed'
          ? 'text-text-muted bg-surface-3'
          : 'text-text-secondary bg-surface-3';

  return (
    <Link
      to={`/plays/${pos.id}`}
      className="flex items-baseline gap-2 flex-wrap rounded border border-border bg-surface-2 hover:border-accent hover:bg-surface-3 transition-colors px-3 py-2 text-xs font-mono no-underline"
    >
      <span className="text-text-primary font-semibold">
        {pos.ticker} {pos.strike}{pos.option_type}
      </span>
      {expiry && <span className="text-text-secondary">{expiry}</span>}
      <span className={clsx('px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider', statusColor)}>
        {pos.status}
      </span>
      <span className="text-accent ml-auto">→</span>
    </Link>
  );
}

function KV({ k, v }: { k: string; v: unknown }) {
  return (
    <div>
      <div className="text-text-muted uppercase tracking-wider text-[10px]">{k}</div>
      <div className="text-text-primary">{v == null || v === '' ? '—' : String(v)}</div>
    </div>
  );
}

function stringifyOr(v: unknown, fallback: string): string {
  if (v == null || v === '') return fallback;
  return String(v);
}

function formatPrice(v: unknown): string {
  if (typeof v === 'number') return `$${v.toFixed(2)}`;
  return '—';
}

/**
 * LLM returns expiry as a structured object (signal-parser-prompt.ts "Expiry
 * Detection"). Render a short human-readable form. Types: 0dte, dte (+days),
 * exact_date (+month, day?, year?), weekly.
 */
function formatExpirySpec(spec: Record<string, unknown> | null | undefined): string | null {
  if (!spec) return null;
  const type = typeof spec.type === 'string' ? spec.type : null;

  if (type === '0dte') return '0DTE';
  if (type === 'weekly') return 'weekly';

  if (type === 'dte' && typeof spec.days === 'number') {
    return `${spec.days}DTE`;
  }

  if (type === 'exact_date') {
    const month = typeof spec.month === 'number' ? spec.month : null;
    const day = typeof spec.day === 'number' ? spec.day : null;
    const year = typeof spec.year === 'number' ? spec.year : null;
    if (month && day && year) {
      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    if (month && day) {
      return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
    if (month && year) {
      return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
    }
    return null;
  }

  return null;
}

export default function PlayDetailPage() {
  return (
    <ErrorBoundary>
      <PlayDetailContent />
    </ErrorBoundary>
  );
}
